// Typed wrappers of the dependency setup (decision D37.2; apps/api/src/routes/dependencies.ts) and of the prepare command
// edit (PATCH /v1/projects/{id} with the workspaces, from the signed-in session — an API token gets 403
// prepare_session_only). The command execution policy is never sent (the API refuses it and keeps the stored one).
import { api } from "./client.ts";
import type { WorkspaceRow } from "./endpoints-blocks.ts";

const enc = encodeURIComponent;

export type DependencyCauseCode = "package_manager_missing" | "prepare_failed" | "prepare_not_configured" | "registry_unreachable";

export interface PrepareSuggestion {
  command: string;
  lockfile: string;
  manager: "pnpm" | "npm" | "yarn";
}

export interface WorkspaceDependencySetup {
  root_path: string;
  subpath: string;
  prepare: string | null;
  allow_install_scripts: boolean;
  lockfiles: string[];
  package_manager: string | null;
  suggestions: PrepareSuggestion[];
  package_managers: { pnpm: string[]; yarn: string[] } | null;
  registry_verified: boolean;
  last: { status: "installed" | "failed"; code: DependencyCauseCode | null; at: string; package_manager: { wanted: string | null; used: string | null; fallback: boolean } | null } | null;
  worker: string | null;
}

export const getDependencySetup = (projectId: string, signal?: AbortSignal) =>
  api<{ workspaces: WorkspaceDependencySetup[] }>(`/projects/${enc(projectId)}/dependency-setup`, signal ? { signal } : {});

/** Sets the prepare command and the install-scripts opt-in of each workspace (empty = no prepare). */
export const savePrepareCommands = (projectId: string, expectedVersion: number, workspaces: readonly WorkspaceRow[], values: readonly { prepare: string; allowScripts: boolean }[]) =>
  api<unknown>(`/projects/${enc(projectId)}`, {
    method: "PATCH",
    body: {
      expected_version: expectedVersion,
      workspaces: workspaces.map((w, i) => {
        const { execution_policy: _policy, ...commands } = w.commands;
        const next: Record<string, unknown> = { ...commands };
        const v = values[i];
        if (v) {
          const prepare = v.prepare.trim();
          if (prepare === "") delete next["prepare"];
          else next["prepare"] = prepare;
          if (v.allowScripts) next["allow_install_scripts"] = true;
          else delete next["allow_install_scripts"];
        }
        return { runner_id: w.runner_id, operating_system: w.operating_system, root_path: w.root_path, subpath: w.subpath, commands: next };
      }),
    },
  });
